import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from "react";

export interface PlaygroundTourState {
  isActive: boolean;
  currentStep: number;
  isModeSwitcherOpen: boolean;
  isModelPopoverOpen: boolean;
  hasFakeOutput: boolean;
  start: () => void;
  end: () => void;
  setStep: (step: number) => void;
  setModeSwitcherOpen: (open: boolean) => void;
  setModelPopoverOpen: (open: boolean) => void;
  setHasFakeOutput: (value: boolean) => void;
}

const PlaygroundTourContext = createContext<PlaygroundTourState | null>(null);

interface PlaygroundTourProviderProps {
  children: ReactNode;
  onStart?: () => void;
  onEnd?: () => void;
}

export const PlaygroundTourProvider = ({
  children,
  onStart,
  onEnd,
}: PlaygroundTourProviderProps) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isModeSwitcherOpen, setModeSwitcherOpen] = useState(false);
  const [isModelPopoverOpen, setModelPopoverOpen] = useState(false);
  const [hasFakeOutput, setHasFakeOutput] = useState(false);

  const start = useCallback(() => {
    setIsActive(true);
    setCurrentStep(0);
    onStart?.();
  }, [onStart]);

  const end = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
    setModeSwitcherOpen(false);
    setModelPopoverOpen(false);
    setHasFakeOutput(false);
    onEnd?.();
  }, [onEnd]);

  const value = useMemo<PlaygroundTourState>(
    () => ({
      isActive,
      currentStep,
      isModeSwitcherOpen,
      isModelPopoverOpen,
      hasFakeOutput,
      start,
      end,
      setStep: setCurrentStep,
      setModeSwitcherOpen,
      setModelPopoverOpen,
      setHasFakeOutput,
    }),
    [isActive, currentStep, isModeSwitcherOpen, isModelPopoverOpen, hasFakeOutput, start, end],
  );

  return <PlaygroundTourContext.Provider value={value}>{children}</PlaygroundTourContext.Provider>;
};

export const usePlaygroundTourContext = (): PlaygroundTourState => {
  const ctx = useContext(PlaygroundTourContext);
  if (!ctx) {
    throw new Error("usePlaygroundTourContext must be used within a PlaygroundTourProvider");
  }
  return ctx;
};

export const useOptionalPlaygroundTourContext = (): PlaygroundTourState | null =>
  useContext(PlaygroundTourContext);
